import { useTranslation } from 'react-i18next';
import { useSession } from '@web/state/session.tsx';
import { TextInput } from '@web/components/ui.tsx';
import { RANGE_PRESETS, RANGE_SECTIONS, ToggleButton, type RangePreset, type Section } from './shared.tsx';

/**
 * This location or all of them. People assigned to a single location have no
 * scope to choose, and get nothing; everyone else can widen from the working
 * location to all of theirs — exactly the dashboard's toggle.
 */
export function ReportScopeToggle({
  choice,
  onChange,
}: {
  choice: 'location' | 'all';
  onChange: (choice: 'location' | 'all') => void;
}) {
  const { t } = useTranslation();
  const { currentLocation, locations } = useSession();
  if (locations.length < 2) return null;
  return (
    <div
      role="group"
      aria-label={t('reports.scopeLabel')}
      className="inline-flex flex-wrap rounded-md border border-[color:var(--color-line)]"
    >
      <ToggleButton active={choice === 'location'} onClick={() => onChange('location')}>
        {currentLocation
          ? t('reports.scopeThisLocationNamed', { name: currentLocation.name })
          : t('reports.scopeThisLocation')}
      </ToggleButton>
      <ToggleButton active={choice === 'all'} onClick={() => onChange('all')}>
        {t('reports.scopeAllLocations')}
      </ToggleButton>
    </div>
  );
}

/**
 * The date range presets, and the two date fields when the range is custom —
 * or, for a report the range does not filter, a line saying so.
 */
export function ReportRangeControls({
  section,
  preset,
  onPreset,
  customFrom,
  customTo,
  onCustomFrom,
  onCustomTo,
}: {
  section: Section;
  preset: RangePreset;
  onPreset: (preset: RangePreset) => void;
  customFrom: string;
  customTo: string;
  onCustomFrom: (value: string) => void;
  onCustomTo: (value: string) => void;
}) {
  const { t } = useTranslation();
  if (!RANGE_SECTIONS.has(section)) {
    return (
      <p className="mb-6 text-sm text-[color:var(--color-muted)]">
        {section === 'capacity' ? t('reports.capacity.rangeHint') : t('reports.snapshotHint')}
      </p>
    );
  }
  return (
    <div className="mb-6 flex flex-wrap items-start gap-x-3 gap-y-0">
      <div
        role="group"
        aria-label={t('reports.rangeLabel')}
        className="inline-flex flex-wrap rounded-md border border-[color:var(--color-line)]"
      >
        {RANGE_PRESETS.map((value) => (
          <ToggleButton key={value} active={preset === value} onClick={() => onPreset(value)}>
            {t(`reports.range.${value}`)}
          </ToggleButton>
        ))}
      </div>
      {preset === 'custom' ? (
        <div className="flex flex-wrap items-start gap-3 [&>div]:mb-0">
          <TextInput
            label={t('reports.rangeFrom')}
            type="date"
            value={customFrom}
            onChange={(event) => onCustomFrom(event.target.value)}
          />
          <TextInput
            label={t('reports.rangeTo')}
            type="date"
            value={customTo}
            onChange={(event) => onCustomTo(event.target.value)}
          />
        </div>
      ) : null}
    </div>
  );
}
